/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { updateProfile, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  orderBy,
  limit,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  addDoc,
  getCountFromServer,
  runTransaction,
  increment, // currently unused, safe but you can remove if eslint warns
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import DoctorCalendarView from "./components/doctor-dashboard/DoctorCalendarView";
import DoctorHeader from "./components/doctor-dashboard/DoctorDashboardHeader";
import DoctorStatsGrid from "./components/doctor-dashboard/StatsGrid";
import DoctorTodaysAppointments from "./components/doctor-dashboard/TodayAppointments";
import AppointmentManagement from "./components/doctor-dashboard/AppointmentManager";
import ResearchLibrary from "./components/doctor-dashboard/ResearchLibrary";
import MedicalNotesModal from "./components/doctor-dashboard/MedicalNotesModal";
import UploadPanel from "./components/doctor-dashboard/UploadPanel";
import AppFooter from "./components/doctor-dashboard/AppFooter";
import { sendTestEmail } from "./sendEmail";
import TelehealthInbox from "./components/telehealth/TelehealthInbox";
import PromoSpot from "./components/common/PromoSpot";
import DoctorBusyManager from "./components/doctor-dashboard/DoctorBusyManager"

import { Calendar, Users, Home, FileText, Plus, MessageCircle } from "lucide-react";

/** Design tokens */
const tokens = {
  bg: "#ffffff",
  cardBg: "#ffffff",
  cardBorder: "#e0e0e0",
  text: "#1f2328",
  textSubtle: "#616161",
  blue: "#0a66c2",
  radius: 8,
  maxw: 1128,
  shadow: "none",
  sectionPad: 16,
  h2: { fontSize: 16, fontWeight: 600, color: "#1f2328", margin: 0 },
};
const MAX_FILES = 3;
const MAX_VIDEO_SECONDS = 15;

/** Drawer button style (used inside the settings drawer) */
const btnItem = {
  textAlign: "left",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
};

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const show = useCallback((content, variant = "info", ttl = 2600) => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, content, variant }]);
    const tm = setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl);
    return () => clearTimeout(tm);
  }, []);
  const remove = (id) => setToasts((t) => t.filter((x) => x.id !== id));
  return { toasts, show, remove };
}
const ToastHost = ({ toasts, onClose }) => (
  <div
    aria-live="polite"
    aria-atomic="true"
    className="toast-host"
    style={{
      position: "fixed",
      right: 20,
      bottom: 92,
      zIndex: 70,
      display: "grid",
      gap: 8,
    }}
  >
    {toasts.map((t) => (
      <div
        key={t.id}
        role="status"
        style={{
          padding: "12px 14px",
          borderRadius: tokens.radius,
          background:
            t.variant === "error"
              ? "#feecec"
              : t.variant === "success"
              ? "#e8f4ff"
              : "#f6f7f8",
          border: `1px solid ${tokens.cardBorder}`,
          color: tokens.text,
          fontSize: 14,
          fontWeight: 600,
          boxShadow: tokens.shadow,
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 240,
          maxWidth: 420,
        }}
      >
        <span>{t.content}</span>
        <button
          onClick={() => onClose(t.id)}
          aria-label="Close"
          style={{
            marginLeft: "auto",
            border: 0,
            background: "transparent",
            cursor: "pointer",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    ))}
  </div>
);

const ErrorBanner = ({ message, onClose }) =>
  !message ? null : (
    <div
      role="alert"
      style={{
        margin: "12px auto 0",
        maxWidth: tokens.maxw,
        padding: "10px 12px",
        borderRadius: tokens.radius,
        background: "#fff",
        border: `1px solid ${tokens.cardBorder}`,
        color: "#b42318",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <strong style={{ fontWeight: 700 }}>Problem</strong>
      <span style={{ fontWeight: 600 }}>{message}</span>
      <button
        onClick={onClose}
        aria-label="Dismiss"
        style={{
          marginLeft: "auto",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          fontWeight: 800,
          color: "#b42318",
        }}
      >
        ×
      </button>
    </div>
  );

const CompleteEnhancedDoctorDashboard = () => {
  const [user, setUser] = useState(null);
  const pushTriedRef = useRef(false);
  const [doctorProfile, setDoctorProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [hardLoading, setHardLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bookings, setBookings] = useState([]);
  const [uploads, setUploads] = useState([]);

  const [colleagues, setColleagues] = useState([]);
  const [colleagueFeed, setColleagueFeed] = useState({}); // { [doctorId]: ActivityItem[] }
  const [colleaguesLoading, setColleaguesLoading] = useState(false);
  const [peerScope, setPeerScope] = useState("clinic"); // 'clinic' | 'same' | 'all' | 'bySpecialty'
  const [peerSpecialty, setPeerSpecialty] = useState("");
  const [allSpecialties, setAllSpecialties] = useState([]);

  const [doctorPromos, setDoctorPromos] = useState([]);
  const [chosenDoctorPromo, setChosenDoctorPromo] = useState(null);
  const promoImpressionRef = useRef(null); // 👈 guard to avoid double impression logs

  // ⬇️ removed modal flags; upload will be its own page
  // const [showUploadModal, setShowUploadModal] = useState(false);
  // const [openUploadPicker, setOpenUploadPicker] = useState(null);
  // const shouldAutoOpenPickerRef = useRef(false);

  const [stats, setStats] = useState({
    totalBookings: 0,
    uploadsCount: 0,
    profileViews: 0,
    pendingBookings: 0,
    todaysConfirmed: 0,
  });

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [medicalData, setMedicalData] = useState({
    symptoms: "",
    diagnosis: "",
    notes: "",
    prescriptions: [],
    followUpInstructions: "",
    nextAppointment: "",
  });
  const [newPrescription, setNewPrescription] = useState({
    medication: "",
    dosage: "",
    frequency: "",
    duration: "",
    instructions: "",
  });

  const [uploadForm, setUploadForm] = useState({
    title: "",
    description: "",
    files: [],
    isUploading: false,
  });

  const [statusFilter, setStatusFilter] = useState("");

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  const [activeSection, setActiveSection] = useState("home");
  const [unreadMessages, setUnreadMessages] = useState(0);
  // === Settings drawer state ===
  const [showConfig, setShowConfig] = useState(false);
  const openConfig = () => setShowConfig(true);
  const closeConfig = () => setShowConfig(false);

  const navigate = useNavigate();
  const { toasts, show: showToast, remove: removeToast } = useToasts();

  const subscribeToUnreadMessages = useCallback((doctorId) => {
    // listen to all conversations of this doctor
    const convRef = collection(db, "conversations");
    const qConv = query(convRef, where("doctorId", "==", doctorId));

    const unsub = onSnapshot(
      qConv,
      (snap) => {
        let total = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          // unreadDoctor is incremented when PATIENT sends a new message
          total += data.unreadDoctor || 0;
        });
        setUnreadMessages(total);
      },
      (err) => {
        console.warn("Failed to listen to unread messages:", err);
      }
    );

    return unsub;
  }, []);

  // ─── Promo helpers ─────────────────────────────────────────────
  const fallbackDoctorPromo = {
    id: "fallback-doctor-telehealth-guide", // give it an id so we can track impressions
    topLabel: "MIKDOK FOKUS",
    title: "Provo konsultat online me pacientët e tu.",
    subtitle: "Aktivo telemjekësinë dhe menaxho më lehtë oraret.",
    ctaLabel: "Shiko udhëzuesin",
    ctaPath: "/doctor/telehealth-guide", // ose çfarëdo rruge ke
  };

  const isPromoActive = (promo) => {
    if (!promo) return false;
    if (promo.active === false) return false;

    const now = new Date();

    if (promo.validFrom) {
      const from = new Date(promo.validFrom);
      from.setHours(0, 0, 0, 0);
      if (now < from) return false;
    }

    if (promo.validTo) {
      const to = new Date(promo.validTo);
      to.setHours(23, 59, 59, 999);
      if (now > to) return false;
    }

    return true;
  };

  const shuffle = (arr = []) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // ─── Promo tracking helpers ───────────────────────────────────────
  const trackDoctorPromoEvent = useCallback(
    async (promo, type) => {
      try {
        if (!promo?.id || !user?.uid) return;

        const eventsRef = collection(db, "promoEvents");

        const payload = {
          promoId: promo.id,
          slot: promo.slot || "doctor-home-main",
          audience: "doctor",
          doctorId: user.uid,
          type, // "impression" | "view" | "click"
          createdAt: serverTimestamp(),
        };

        await addDoc(eventsRef, payload);
      } catch (err) {
        console.warn("[promo metrics] Failed to track promo event", type, err);
      }
    },
    [user]
  );

  // Listen live to promos targeted to doctors
  useEffect(() => {
    const promoQ = query(
      collection(db, "promoSlots"),
      where("audience", "==", "doctor"),
      where("slot", "==", "doctor-home-main"),
      orderBy("priority", "asc")
    );

    const unsub = onSnapshot(
      promoQ,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDoctorPromos(list);
      },
      (err) => {
        console.error("[doctor promos] Error loading promos:", err);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const active = doctorPromos.filter(isPromoActive);
    const list = active.length > 0 ? active : [fallbackDoctorPromo];

    if (list.length === 0) {
      setChosenDoctorPromo(null);
      return;
    }

    const selected = shuffle(list)[0];
    setChosenDoctorPromo(selected);
  }, [doctorPromos]);

  const handleDoctorPromoClick = (promo) => {
    if (!promo) return;
    const targetType =
      promo.targetType || (promo.externalUrl ? "external" : "internal");

    if (targetType === "external" && promo.externalUrl) {
      const newTab = promo.openInNewTab ?? true;
      window.open(promo.externalUrl, newTab ? "_blank" : "_self");
      return;
    }

    if (promo.ctaPath) {
      navigate(promo.ctaPath);
    }
  };

  // 🔁 Impression tracking – log at most once per promo id per session
  useEffect(() => {
    if (!chosenDoctorPromo || !user) return;
    if (!chosenDoctorPromo.id) return;

    // if we already logged this promo id, skip (prevents StrictMode double-effect, etc.)
    if (promoImpressionRef.current === chosenDoctorPromo.id) return;

    promoImpressionRef.current = chosenDoctorPromo.id;
    trackDoctorPromoEvent(chosenDoctorPromo, "impression");
  }, [chosenDoctorPromo, user, trackDoctorPromoEvent]);

  /** Global CSS + mobile tweaks */
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      html { scroll-behavior: smooth; }
      @media (prefers-reduced-motion: reduce) {
        * { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
      }
      .sticky-top { position: sticky; top: 0; z-index: 40; background: #fff; border-bottom: 1px solid ${tokens.cardBorder}; }
      .shell { max-width: ${tokens.maxw}px; margin: 0 auto; padding: 16px; padding-bottom: 96px; }
      .card { background: ${tokens.cardBg}; border: 1px solid ${tokens.cardBorder}; border-radius: ${tokens.radius}px; box-shadow: ${tokens.shadow}; }
      .card-pad { padding: ${tokens.sectionPad}px; }
      @media (max-width:480px){
        .toast-host { left: 50%; right: auto !important; transform: translateX(-50%); bottom: 96px; width: calc(100vw - 24px); max-width: 480px; }
        .modal-sheet { width: 100% !important; max-width: none !important; height: 92vh; border-radius: 12px 12px 0 0 !important; }
      }
    `;
    document.head.appendChild(style);
    const t = setTimeout(() => setHardLoading(false), 250);
    return () => {
      document.head.removeChild(style);
      clearTimeout(t);
    };
  }, []);

  const getTodayString = () => new Date().toISOString().split("T")[0];
  const formatDate = (date) =>
    new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  const formatTime = (timeString) => {
    if (!timeString) return "Time TBD";
    try {
      return new Date(`2000-01-01 ${timeString}`).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return timeString;
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return {
          backgroundColor: "#e8f4ff",
          color: "#004182",
          borderColor: "#d0e6ff",
        };
      case "pending":
        return {
          backgroundColor: "#fff7e6",
          color: "#8a5a00",
          borderColor: "#ffe3b3",
        };
      case "completed":
        return {
          backgroundColor: "#f1f8f5",
          color: "#1a4d38",
          borderColor: "#d7ece2",
        };
      case "cancelled":
        return {
          backgroundColor: "#feecec",
          color: "#7f1d1d",
          borderColor: "#f7d4d4",
        };
      default:
        return {
          backgroundColor: "#f6f7f8",
          color: "#434343",
          borderColor: "#e0e0e0",
        };
    }
  };

  const fetchDoctorProfile = useCallback(
    async (doctorId) => {
      try {
        const doctorRef = doc(db, "doctors", doctorId);
        const snap = await getDoc(doctorRef);
        if (!snap.exists()) {
          navigate("/doctor-profile-quick");
          return false;
        }
        const profile = snap.data();
        setDoctorProfile(profile);
        if (!profile.name || !profile.specialty) {
          navigate("/doctor-profile-quick");
          return false;
        }
        const verificationRef = doc(db, "verifications", doctorId);
        const ver = await getDoc(verificationRef);
        const isVerified = ver.exists() && ver.data().status === "approved";
        if (!isVerified) {
          navigate("/verify-doctor");
          return false;
        }
        return true;
      } catch {
        setError("Failed to load profile. Please try again.");
        return false;
      }
    },
    [navigate]
  );

  const fetchColleagues = useCallback(
    async (doctorId, profile) => {
      setColleaguesLoading(true);
      try {
        // Prefer same clinic
        let qBase;
        if (profile?.clinicId) {
          qBase = query(
            collection(db, "doctors"),
            where("clinicId", "==", profile.clinicId),
            limit(30)
          );
        } else {
          // Fallback: same specialty (optionally filter by city if you store it)
          qBase = query(
            collection(db, "doctors"),
            where("specialty", "==", profile?.specialty || ""),
            limit(30)
          );
        }

        const snap = await getDocs(qBase);
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (d) =>
              d.id !== doctorId && (d.verified === true || d.verified === undefined)
          );

        setColleagues(rows);
        return rows;
      } catch {
        showToast("Failed to load colleagues", "error");
        return [];
      } finally {
        setColleaguesLoading(false);
      }
    },
    [showToast]
  );

  const fetchColleagueActivity = useCallback(
    async (docId) => {
      // Pull a small, cheap feed: 2 recent appointments + 1 recent upload
      try {
        const apptQ = query(
          collection(db, "appointments"),
          where("doctorId", "==", docId),
          where("status", "in", ["completed", "confirmed"]),
          orderBy("createdAt", "desc"),
          limit(2)
        );

        const uploadsQ = query(
          collection(db, "uploads", docId, "items"),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const [apptSnap, uploadSnap] = await Promise.all([
          getDocs(apptQ),
          getDocs(uploadsQ),
        ]);

        const apptItems = apptSnap.docs.map((d) => {
          const a = d.data();
          return {
            type: a.status === "completed" ? "completed" : "confirmed",
            at: a.createdAt?.toDate?.() || new Date(),
            summary:
              a.status === "completed"
                ? "Completed an appointment"
                : "Confirmed a booking",
          };
        });

        const upItems = uploadSnap.docs.map((d) => ({
          type: "upload",
          at: d.data().createdAt?.toDate?.() || new Date(),
          summary: `Uploaded: ${d.data().title || "New resource"}`,
        }));

        const items = [...apptItems, ...upItems]
          .sort((a, b) => b.at - a.at)
          .slice(0, 3);

        setColleagueFeed((prev) => ({ ...prev, [docId]: items }));
      } catch {
        // silent; it's a nice-to-have
      }
    },
    []
  );

  const ActivityChip = ({ item }) => {
    const base = {
      padding: "6px 10px",
      borderRadius: 999,
      border: `1px solid ${tokens.cardBorder}`,
      background: "#f6f7f8",
      fontSize: 12,
      fontWeight: 600,
      color: tokens.textSubtle,
      whiteSpace: "nowrap",
    };
    const label =
      item.type === "completed"
        ? "Completed"
        : item.type === "confirmed"
        ? "Confirmed"
        : "Upload";
    return (
      <span title={item.summary} style={base}>
        {label}
      </span>
    );
  };

  const ColleagueCard = ({ c, feed }) => {
    return (
      <div className="card" style={{ padding: 12, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <img
            src={c.photoURL || "/avatar.png"}
            alt=""
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              objectFit: "cover",
              border: `1px solid ${tokens.cardBorder}`,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: tokens.text }}>
              {c.name || "Doctor"}
            </div>
            <div
              style={{
                color: tokens.textSubtle,
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {c.specialty || "Specialty"}
              {c.location?.city ? ` • ${c.location.city}` : ""}
            </div>
          </div>
          {/* no booking actions here */}
        </div>

        {feed?.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {feed.map((it, i) => (
              <ActivityChip key={i} item={it} />
            ))}
          </div>
        ) : (
          <div
            style={{
              color: tokens.textSubtle,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            No recent activity
          </div>
        )}
      </div>
    );
  };

  const safeCount = async (q) => {
    try {
      const r = await getCountFromServer(q);
      return r.data().count || 0;
    } catch {
      return 0;
    }
  };
  const fetchAppointmentStats = async (doctorId) => {
    const baseQ = query(
      collection(db, "appointments"),
      where("doctorId", "==", doctorId)
    );
    const byStatus = (status) =>
      safeCount(
        query(
          collection(db, "appointments"),
          where("doctorId", "==", doctorId),
          where("status", "==", status)
        )
      );
    const [all, pending, confirmed, completed, cancelled] = await Promise.all([
      safeCount(baseQ),
      byStatus("pending"),
      byStatus("confirmed"),
      byStatus("completed"),
      byStatus("cancelled"),
    ]);
    return { all, pending, confirmed, completed, cancelled };
  };

  const filterTodaysAppointments = useCallback((appointments) => {
    const todayString = getTodayString();
    const todays = appointments
      .filter(
        (a) => a.status === "confirmed" && a.scheduledDate === todayString
      )
      .sort((a, b) =>
        !a.scheduledTime || !b.scheduledTime
          ? 0
          : a.scheduledTime.localeCompare(b.scheduledTime)
      );
    setStats((s) => ({ ...s, todaysConfirmed: todays.length }));
  }, []);

  const fetchBookings = useCallback(
    async (doctorId) => {
      let listQ;
      try {
        listQ = query(
          collection(db, "appointments"),
          where("doctorId", "==", doctorId),
          orderBy("createdAt", "desc"),
          limit(20)
        );
      } catch {
        listQ = query(
          collection(db, "appointments"),
          where("doctorId", "==", doctorId),
          limit(20)
        );
      }
      const unsubscribe = onSnapshot(
        listQ,
        async (snapshot) => {
          const raw = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          const sorted = raw.sort((a, b) => {
            const aD = a.createdAt?.toDate?.() ?? new Date(0);
            const bD = b.createdAt?.toDate?.() ?? new Date(0);
            return bD - aD;
          });
          const detailed = sorted.map((a) => ({
            id: a.id,
            service: a.serviceName || "Unknown Service",
            patientName: a.patientInfo?.name || "Unknown Patient",
            patientEmail: a.patientInfo?.email || "Unknown Email",
            date: a.createdAt?.toDate?.() || new Date(),
            scheduledDate: a.scheduledDate,
            scheduledTime: a.scheduledTime,
            appointmentType: a.appointmentType || "in-person",
            reason: a.reason || "",
            notes: a.notes || "",
            status: a.status || "pending",
            servicePrice: a.servicePrice,
            ...a,
          }));
          setBookings(detailed);
          filterTodaysAppointments(detailed);
          const pendingInPage = detailed.filter(
            (b) => b.status === "pending"
          ).length;
          setStats((s) => ({ ...s, pendingBookings: pendingInPage }));
          try {
            const totals = await fetchAppointmentStats(doctorId);
            setStats((s) => ({
              ...s,
              totalBookings: totals.all,
              pendingBookings: totals.pending,
              confirmedAll: totals.confirmed,
              completedAll: totals.completed,
              cancelledAll: totals.cancelled,
            }));
          } catch {
            // ignore
          }
        },
        (err) => setError(`Failed to load appointments: ${err.message}`)
      );
      return unsubscribe;
    },
    [filterTodaysAppointments]
  );

  const fetchUploads = useCallback(async (doctorId) => {
    try {
      const qy = query(
        collection(db, "uploads", doctorId, "items"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(qy);
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        downloads: d.data().downloads || 0,
      }));
      setUploads(data);
      setStats((s) => ({ ...s, uploadsCount: data.length }));
    } catch {
      setError("Failed to load research uploads.");
    }
  }, []);

  useEffect(() => {
    let bookingsUnsubscribe = null;
    let unbindForeground = null;
    let unreadUnsub = null;
    const offAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        navigate("/login");
        return;
      }
      setUser(firebaseUser);
      setError(null);
      try {
        const ok = await fetchDoctorProfile(firebaseUser.uid);
        if (!ok) {
          setLoading(false);
          return;
        }
        bookingsUnsubscribe = await fetchBookings(firebaseUser.uid);
        await fetchUploads(firebaseUser.uid);
        unreadUnsub = subscribeToUnreadMessages(firebaseUser.uid);
        setStats((s) => ({ ...s, profileViews: 127 }));
        setLoading(false);

        if (!pushTriedRef.current) {
          pushTriedRef.current = true;
          try {
            const { canOfferPush } = await import("./pushPolicy");
            if (await canOfferPush()) {
              const { enablePushCore, bindForegroundPush } = await import(
                "./push"
              );
              enablePushCore(firebaseUser.uid).catch(() => {});
              unbindForeground = bindForegroundPush(() =>
                showToast("New notification", "info")
              );
            }
          } catch {
            // ignore
          }
        }
      } catch {
        setError("Failed to load dashboard. Please try again.");
        setLoading(false);
      }
    });
    setHardLoading(false);
    return () => {
      if (typeof offAuth === "function") offAuth();
      if (typeof bookingsUnsubscribe === "function") bookingsUnsubscribe();
      if (typeof unbindForeground === "function") unbindForeground();
      if (typeof unreadUnsub === "function") unreadUnsub();
    };
  }, [navigate, fetchDoctorProfile, fetchBookings, fetchUploads, showToast]);

  const updateAppointmentStatus = async (
    appointmentId,
    newStatus,
    cancelReason // optional
  ) => {
    try {
      const appointmentRef = doc(db, "appointments", appointmentId);

      // 1) Read current appointment
      const snap = await getDoc(appointmentRef);
      if (!snap.exists()) {
        console.error(
          "updateAppointmentStatus: appointment not found",
          appointmentId
        );
        return;
      }

      const data = snap.data() || {};
      const { doctorId, scheduledDate, scheduledTime } = data;

      // 2) Build appointment update payload
      const payload = {
        status: newStatus,
        updatedAt: serverTimestamp(),
      };

      if (newStatus === "confirmed") {
        payload.confirmedAt = serverTimestamp();
      }

      if (newStatus === "completed") {
        payload.completedAt = serverTimestamp();
      }

      if (newStatus === "cancelled") {
        payload.cancelledAt = serverTimestamp();
        payload.cancelReason = cancelReason || "";
      }

      console.log("updateAppointmentStatus -> appointment payload", {
        appointmentId,
        newStatus,
        payload,
      });

      // 3) Update appointment (this should trigger your onSnapshot in fetchBookings)
      await updateDoc(appointmentRef, payload);

      // 4) Sync lock status (public) – best effort, but NOT inside a transaction
      if (doctorId && scheduledDate && scheduledTime) {
        const lockId = `${doctorId}_${scheduledDate}_${scheduledTime}`;
        const lockRef = doc(db, "locks", lockId);

        try {
          const lockSnap = await getDoc(lockRef);
          if (lockSnap.exists()) {
            await updateDoc(lockRef, {
              status: newStatus === "cancelled" ? "cancelled" : newStatus,
              updatedAt: serverTimestamp(),
            });

            console.log("updateAppointmentStatus -> lock updated", {
              lockId,
              status: newStatus === "cancelled" ? "cancelled" : newStatus,
            });
          } else {
            console.log(
              "updateAppointmentStatus: lock not found, nothing to sync",
              lockId
            );
          }
        } catch (lockErr) {
          console.warn(
            "updateAppointmentStatus: appointment updated but failed to update lock",
            lockErr
          );
        }
      } else {
        console.log(
          "updateAppointmentStatus: no doctorId/scheduledDate/scheduledTime on appointment; skipping lock sync"
        );
      }
    } catch (err) {
      console.error("Error updating appointment status", err);
    }
  };

  const saveMedicalRecord = async () => {
    if (!selectedAppointment || !user) {
      showToast("Missing appointment or user", "error");
      return;
    }
    try {
      const medicalRecordData = {
        appointmentId: selectedAppointment.id,
        patientId: selectedAppointment.patientId,
        doctorId: user.uid,
        patientInfo: selectedAppointment.patientInfo,
        doctorInfo: {
          name: doctorProfile?.name,
          specialty: doctorProfile?.specialty,
        },
        appointmentDate: selectedAppointment.scheduledDate,
        appointmentTime: selectedAppointment.scheduledTime,
        symptoms: medicalData.symptoms,
        diagnosis: medicalData.diagnosis,
        notes: medicalData.notes,
        prescriptions: medicalData.prescriptions,
        followUpInstructions: medicalData.followUpInstructions,
        nextAppointment: medicalData.nextAppointment,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await addDoc(collection(db, "medicalRecords"), medicalRecordData);
      await updateAppointmentStatus(selectedAppointment.id, "completed");
      showToast("Medical record saved", "success");
      setShowMedicalModal(false);
      resetMedicalForm();
    } catch {
      showToast("Failed to save record", "error");
    }
  };

  const resetMedicalForm = () => {
    setMedicalData({
      symptoms: "",
      diagnosis: "",
      notes: "",
      prescriptions: [],
      followUpInstructions: "",
      nextAppointment: "",
    });
    setNewPrescription({
      medication: "",
      dosage: "",
      frequency: "",
      duration: "",
      instructions: "",
    });
    setSelectedAppointment(null);
  };
  const addPrescription = () => {
    if (!newPrescription.medication || !newPrescription.dosage) {
      showToast("Medication and dosage required", "error");
      return;
    }
    setMedicalData((p) => ({
      ...p,
      prescriptions: [
        ...p.prescriptions,
        { id: Date.now().toString(), ...newPrescription },
      ],
    }));
    setNewPrescription({
      medication: "",
      dosage: "",
      frequency: "",
      duration: "",
      instructions: "",
    });
    showToast("Prescription added", "success");
  };
  const removePrescription = (id) => {
    setMedicalData((p) => ({
      ...p,
      prescriptions: p.prescriptions.filter((x) => x.id !== id),
    }));
    showToast("Prescription removed", "success");
  };
  const openMedicalModal = (appointment) => {
    setSelectedAppointment(appointment);
    setShowMedicalModal(true);
  };

 const handleFileChange = (e) => {
  const rawFiles = Array.from(e.target.files || []);
  if (!rawFiles.length) return;

  // Respect MAX_FILES (3)
  const incoming = rawFiles.slice(0, MAX_FILES);

  const imagePdfFiles = incoming.filter(
    (file) => !file.type.startsWith("video/")
  );
  const videoFiles = incoming.filter((file) =>
    file.type.startsWith("video/")
  );

  // If there are no videos, we can store directly
  if (videoFiles.length === 0) {
    setUploadForm((prev) => ({
      ...prev,
      files: imagePdfFiles,
    }));
    return;
  }

  // For videos, we must check duration with a temporary <video> element
  const checks = videoFiles.map(
    (file) =>
      new Promise((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = URL.createObjectURL(file);

        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          const duration = video.duration || 0;

          if (duration <= MAX_VIDEO_SECONDS) {
            resolve(file);
          } else {
            showToast(
              `Video "${file.name}" is longer than ${MAX_VIDEO_SECONDS} seconds and will be skipped.`,
              "error"
            );
            resolve(null);
          }
        };

        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          showToast(
            `Could not read video file "${file.name}".`,
            "error"
          );
          resolve(null);
        };
      })
  );

  Promise.all(checks).then((validVideos) => {
    const filteredVideos = validVideos.filter(Boolean);
    const finalFiles = [...imagePdfFiles, ...filteredVideos].slice(
      0,
      MAX_FILES
    );

    setUploadForm((prev) => ({
      ...prev,
      files: finalFiles,
    }));
  });
};

  const handleUpload = useCallback(
  async () => {
    if (
      !user ||
      !uploadForm.title?.trim() ||
      !uploadForm.description?.trim() ||
      uploadForm.files.length === 0
    ) {
      showToast("Complete all upload fields", "error");
      return;
    }

    const hasVideo = uploadForm.files.some((f) =>
      f.type.startsWith("video/")
    );

    setUploadForm((p) => ({ ...p, isUploading: true }));

    try {
      const uploadId = Date.now().toString();
      const fileUrls = [];

      for (const file of uploadForm.files) {
        const safeName = file.name.replace(/[^\w.\-]/g, "_");
        const path = `doctor_uploads/${user.uid}/${uploadId}/${safeName}`;
        const sref = ref(storage, path);

        const isVideo = file.type.startsWith("video/");
        const metadata = {
          contentType:
            file.type ||
            (isVideo ? "video/mp4" : "application/octet-stream"),
        };

        const snap = await uploadBytes(sref, file, metadata);
        const url = await getDownloadURL(sref); // same as snap.ref
        fileUrls.push(url);
      }

      const uploadDoc = doc(db, "uploads", user.uid, "items", uploadId);
      await setDoc(uploadDoc, {
        title: uploadForm.title.trim(),
        description: uploadForm.description.trim(),
        files: fileUrls,               // will be replaced/augmented by the transcoder for videos
        createdAt: serverTimestamp(),  // was new Date()
        downloads: 0,
        doctorId: user.uid,
        hasVideo,
        videoProcessing: hasVideo ? "pending" : "none",
      });

      setUploadForm({
        title: "",
        description: "",
        files: [],
        isUploading: false,
      });

      await fetchUploads(user.uid);
      showToast("Upload successful", "success");
    } catch (err) {
      console.error("Upload failed", err);
      showToast("Upload failed", "error");
      setUploadForm((p) => ({ ...p, isUploading: false }));
    }
  },
  [user, uploadForm, fetchUploads, showToast]
);


  const handleViewProfile = () => user && window.open(`/doctor/${user.uid}`, "_blank");
  const handleEditProfile = () => navigate("/setup-doctor-profile");

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!user || !file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Select an image file", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("Max size 5MB", "error");
      e.target.value = "";
      return;
    }

    setUploadingPhoto(true);
    try {
      const safe = file.name.replace(/[^\w.\-]/g, "_");
      const path = `doctor_photos/${user.uid}/${Date.now()}_${safe}`;
      const sref = ref(storage, path);
      const snap = await uploadBytes(sref, file, { contentType: file.type });
      const photoURL = await getDownloadURL(snap.ref);
      await updateDoc(doc(db, "doctors", user.uid), {
        photoURL,
        updatedAt: serverTimestamp(),
      });
      if (auth.currentUser) await updateProfile(auth.currentUser, { photoURL });
      setDoctorProfile((p) => ({ ...p, photoURL }));
      showToast("Profile photo updated", "success");
    } catch {
      showToast("Photo upload failed", "error");
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };
  const openPhotoPicker = () => fileInputRef.current?.click();

  const filteredBookings = !statusFilter
    ? bookings
    : bookings.filter((b) => b.status === statusFilter);

  // Reusable logout (used by header & drawer)
  const logout = async () => {
    try {
      await auth.signOut();
      navigate("/login");
    } catch {
      showToast("Failed to sign out", "error");
    }
  };
    // Helper: open Patients tab with a predefined status
  const openPatientsWithStatus = useCallback(
    (status) => {
      setStatusFilter(status);
      setActiveSection("patients");
    },
    []
  );


  /* Fixed-page section renderer */
  const renderSection = () => {
    switch (activeSection) {
      case "home":
        return (
          <div className="shell">
            <section className="card" style={{ marginBottom: 12 }}>
              <div className="card-pad">
                <div style={{ ...tokens.h2, marginBottom: 10 }}>Overview</div>
                <DoctorStatsGrid
  stats={stats}
  onClickPending={() => openPatientsWithStatus("pending")}
  onClickToday={() => openPatientsWithStatus("pending")}
/>

              </div>
            </section>

            {chosenDoctorPromo && (
              <section
                className="card"
                style={{ marginBottom: 12, padding: 10 }}
              >
                <PromoSpot
                  promo={chosenDoctorPromo}
                  onCtaClick={handleDoctorPromoClick}
                  onExpand={(promo) => trackDoctorPromoEvent(promo, "view")}
                  onClickTrack={(promo) => trackDoctorPromoEvent(promo, "click")}
                />
              </section>
            )}

            <DoctorCalendarView
              bookings={bookings}
              onOpenMedicalNotes={openMedicalModal}
              onUpdateAppointmentStatus={updateAppointmentStatus}
            />
          </div>
        );

    case "today":
  return (
    <div className="shell" style={{ background: "#fff" }}>
      {/* Today’s appointments list */}
      <section
        style={{
          background: "#fff",
          border: "none",
          borderRadius: 0,
          marginBottom: 12,
        }}
      >
        <div className="card-pad" style={{ padding: 0 }}>
          <DoctorTodaysAppointments
            bookings={bookings}
            onOpenMedicalNotes={openMedicalModal}
          />
        </div>
      </section>

      {/* Block not-free time section */}
      <section className="card">
        <div className="card-pad">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <h2 style={tokens.h2}>Block time you’re not available</h2>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: tokens.textSubtle,
              }}
            >
              These periods will appear as unavailable to patients.
            </span>
          </div>

          <DoctorBusyManager
            doctorId={user?.uid}
            showToast={showToast}
            tokens={tokens}
          />
        </div>
      </section>
    </div>
  );

      case "patients":
        return (
          <div className="shell">
            <style>{`
              .am-scroll { 
                max-height: none !important; 
                overflow: visible !important; 
                padding-left: 0; 
                padding-right: 0; 
              }
            `}</style>

            <AppointmentManagement
              bookings={filteredBookings}
              stats={stats}
              statusFilter={statusFilter}
              onChangeStatusFilter={setStatusFilter}
              onUpdateAppointmentStatus={updateAppointmentStatus}
              getStatusColor={getStatusColor}
              formatDate={formatDate}
              formatTime={formatTime}
            />
          </div>
        );

      case "messages":
        return (
          <div className="shell">
            <TelehealthInbox mode="doctor" />
          </div>
        );

      case "research":
        return (
          <div className="shell">
            <section className="card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <FileText size={18} color={tokens.blue} />
                <h2 style={tokens.h2}>Research library</h2>
              </div>
              <ResearchLibrary
                uploads={uploads}
                onOpenUpload={(item) => console.log("open upload", item)}
              />
            </section>
          </div>
        );

      case "upload":
        return (
          <div className="shell">
            <section className="card">
              <UploadPanel
                uploadForm={uploadForm}
                onUploadTitleChange={(e) =>
                  setUploadForm((p) => ({ ...p, title: e.target.value }))
                }
                onUploadDescriptionChange={(e) =>
                  setUploadForm((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
                onUploadFilesChange={handleFileChange}
                onSubmitUpload={handleUpload}
              />
            </section>
          </div>
        );

      case "colleagues":
        return (
          <div className="shell">
            <section style={{ marginBottom: 12 }}>
              <div
                className="card card-pad"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Users size={18} color={tokens.blue} />
                <h2 style={tokens.h2}>Peers</h2>
              </div>
            </section>

            {colleaguesLoading && (
              <div
                className="card card-pad"
                style={{
                  fontWeight: 600,
                  color: tokens.textSubtle,
                }}
              >
                Loading colleagues…
              </div>
            )}

            {!colleaguesLoading && colleagues.length === 0 && (
              <div
                className="card card-pad"
                style={{
                  fontWeight: 600,
                  color: tokens.textSubtle,
                }}
              >
                No colleagues found for your clinic/specialty yet.
              </div>
            )}

            <div style={{ display: "grid", gap: 10 }}>
              {colleagues.map((c) => (
                <ColleagueCard key={c.id} c={c} feed={colleagueFeed[c.id]} />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || hardLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: tokens.bg,
          display: "grid",
          placeItems: "center",
          padding: 24,
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div
          style={{
            width: "min(420px, 92vw)",
            background: tokens.cardBg,
            borderRadius: tokens.radius,
            border: `1px solid ${tokens.cardBorder}`,
            boxShadow: tokens.shadow,
            padding: 24,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              border: "3px solid #e0e0e0",
              borderTop: `3px solid ${tokens.blue}`,
              borderRadius: "50%",
              margin: "0 auto 14px",
              animation: "spin 1s linear infinite",
            }}
          />
          <div
            style={{
              fontWeight: 700,
              color: tokens.text,
              fontSize: 16,
              marginBottom: 4,
            }}
          >
            Loading your dashboard
          </div>
          <div
            style={{
              color: tokens.textSubtle,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Fetching appointments and updates…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100vh",
        background: tokens.bg,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: tokens.text,
        display: "grid",
        gridTemplateRows: "64px 1fr 76px", // header, content, footer
        overflow: "hidden",
      }}
    >
      {/* Header row (fixed height) */}
      <div className="sticky-top" style={{ height: 64, display: "grid" }}>
        <DoctorHeader
          user={user}
          doctorProfile={doctorProfile}
          uploadingPhoto={uploadingPhoto}
          onChangePhoto={handleProfilePhotoUpload}
          onAvatarClick={openConfig}
          onLogout={logout}
        />
      </div>

      {/* Content row */}
      <div style={{ overflow: "auto" }}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {renderSection()}
      </div>

      {/* Right-side Settings Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!showConfig}
        style={{ pointerEvents: showConfig ? "auto" : "none" }}
      >
        {/* Scrim */}
        <div
          onClick={closeConfig}
          style={{
            position: "fixed",
            inset: 0,
            background: showConfig ? "rgba(0,0,0,0.35)" : "transparent",
            transition: "background .2s ease",
            zIndex: ninety(),
          }}
        />

        {/* Panel */}
        <aside
          onTouchStart={() => {}}
          onTouchEnd={() => {}}
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: "min(420px, 92vw)",
            background: "#fff",
            borderLeft: "1px solid #e5e7eb",
            boxShadow: "0 10px 40px rgba(0,0,0,.2)",
            transform: showConfig ? "translateX(0)" : "translateX(100%)",
            transition: "transform .24s cubic-bezier(.2,.8,.2,1)",
            zIndex: ninety(),
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: 14,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <strong style={{ fontSize: 14 }}>Settings</strong>
            <button
              type="button"
              onClick={closeConfig}
              style={{
                border: 0,
                background: "transparent",
                fontSize: 22,
                lineHeight: 1,
                cursor: "pointer",
                color: "#6b7280",
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div
            style={{
              padding: 16,
              overflow: "auto",
              display: "grid",
              gap: 10,
            }}
          >
            <button style={btnItem} onClick={handleViewProfile}>
              View public profile
            </button>
            <button style={btnItem} onClick={handleEditProfile}>
              Edit profile
            </button>
            <button style={btnItem} onClick={openPhotoPicker}>
              Change profile photo
            </button>
            <button
              style={btnItem}
              onClick={() => setActiveSection("today")}
            >
              Manage schedule
            </button>
            <button
              style={{
                ...btnItem,
                borderColor: "#fca5a5",
                color: "#b91c1c",
              }}
              onClick={logout}
            >
              Sign out
            </button>
          </div>
        </aside>
      </div>

      {/* Footer row */}
      <div style={{ height: 76 }}>
        <AppFooter
          categories={[
            {
              key: "home",
              label: "Home",
              action: () => setActiveSection("home"),
              Icon: Home,
            },
            {
              key: "today",
              label: "Today",
              action: () => setActiveSection("today"),
              Icon: Calendar,
            },
            {
              key: "patients",
              label: "Patients",
              action: () => setActiveSection("patients"),
              Icon: Users,
            },
            {
              key: "messages",
              label: "Messages",
              action: () => setActiveSection("messages"),
              Icon: MessageCircle,
              badge: unreadMessages > 0 ? unreadMessages : null,
            },
            {
              key: "research",
              label: "Research",
              action: () => setActiveSection("research"),
              Icon: FileText,
            },
            {
              key: "upload",
              label: "Upload",
              action: () => setActiveSection("upload"),
              Icon: Plus,
            },
          ]}
        />
      </div>

      {/* Hidden file input for profile photo */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleProfilePhotoUpload}
        style={{ display: "none" }}
      />

      {/* Medical notes modal */}
      <MedicalNotesModal
        open={showMedicalModal}
        appointment={selectedAppointment}
        onClose={() => setShowMedicalModal(false)}
        onSave={saveMedicalRecord}
        medicalData={medicalData}
        setMedicalData={setMedicalData}
        newPrescription={newPrescription}
        setNewPrescription={setNewPrescription}
        onAddPrescription={addPrescription}
        onRemovePrescription={removePrescription}
        formatTime={formatTime}
      />

      <ToastHost toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default CompleteEnhancedDoctorDashboard;

/** tiny util so the inline style above reads better */
function ninety() {
  return 90;
}
